/**
 * @pms/rates — the market-facing layer.
 *
 * The park's pricing is a SEARCH: a stay is tiled by rates and the guest pays
 * the cheapest legal tiling. Nothing in the distribution world consumes that.
 * Channel managers, OTAs and booking widgets consume ARI — a dense grid of
 *
 *     (unit type, arrival date, length of stay [, occupancy]) -> price
 *
 * so this package MATERIALISES the search into that grid. The programme stays
 * the generator; the grid is the published artefact.
 *
 * ---------------------------------------------------------------------------
 * WHY MATERIALISE INSTEAD OF PRICING ON DEMAND
 * ---------------------------------------------------------------------------
 *
 * Two reasons, both structural rather than performance:
 *
 * 1. **Channels will not call you.** Booking.com, Airbnb and every channel
 *    manager pull a grid on a schedule. There is no protocol in which they ask
 *    your engine to solve a shortest path. If the grid does not exist, the park
 *    cannot be distributed.
 *
 * 2. **A published price must be stable and auditable.** A guest who saw EUR
 *    950 yesterday is owed EUR 950 today, even if a rate was edited overnight.
 *    A materialised cell carries the programme version that produced it, so
 *    "why was this price shown" has an answer.
 *
 * ---------------------------------------------------------------------------
 * WHAT MATERIALISATION IS NOT ALLOWED TO DO
 * ---------------------------------------------------------------------------
 *
 * It must not invent a price the engine would not quote. Every cell is produced
 * by calling `priceStay` for that exact (arrival, los) — never by interpolating
 * from a neighbour, never by assuming additivity. The park's tariffs are
 * explicitly NOT additive: a 14-night stay is not two 7-night stays, which is
 * the whole point of the long-stay tapers. `verifyCells()` re-prices a sample
 * and fails loudly if any cell drifted.
 */

import type { PieceSet, Quote } from '../../tariff/src/types';
import { priceStay } from '../../tariff/src/engine';
import { checkRestrictions, type Restriction } from '../../availability/src/index';

/** Days since 1970-01-01 UTC, matching the engine. */
export type Day = number;

export type CellStatus = 'bookable' | 'onRequest' | 'closed';

/**
 * One published price. Deliberately flat and boring — this is the shape that
 * crosses a wire to a channel, and every field is one a channel understands.
 */
export interface PublishedCell {
  /** Accommodation / unit type this price belongs to. */
  unitType: string;
  /** Arrival date. */
  date: Day;
  /** Length of stay in nights. */
  los: number;
  /** Total for the whole stay, not per night. Absent when not bookable. */
  price?: number;
  status: CellStatus;
  /**
   * Occupancy in the CHANNEL's vocabulary — `2A1C`, `4A`, `2A1C1I`.
   *
   * Every OTA counts a party exactly one way and will not change it, so this
   * is the only form a feed can be indexed by. A park may price five age
   * bands; three of them may be `child` here, and that is not a loss, because
   * the price was already computed in the park's own vocabulary before the
   * key was formed.
   */
  occupancy: string;
  /**
   * The party as the PARK counted it, `key:count` pairs, sorted.
   *
   * Kept beside the channel key because the mapping to it is many-to-one: this
   * is what explains why two cells with the same `occupancy` may differ in
   * price, and it is what a park-side booking screen actually needs.
   */
  party?: string;
  /** Which programme version produced it, so a shown price can be explained. */
  source: string;
}

export interface MaterialiseOptions {
  unitType: string;
  from: Day;
  /** Exclusive. */
  until: Day;
  /** Lengths of stay to publish. Dense, because channels cap LOS anyway. */
  lengths: number[];
  quantities?: Record<string, number>;
  /**
   * The park's stay rules, applied to every cell.
   *
   * ---------------------------------------------------------------------------
   * WHY THE GRID HAS TO CARRY THEM
   * ---------------------------------------------------------------------------
   *
   * A price and a permission are different things, and only the price used to
   * reach a channel. `checkRestrictions` ran on the quote route — the website's
   * path — so the park's own site refused a departure in the middle of the
   * Ascension weekend while the published grid happily offered it. A channel
   * would have sold the stay the park had just forbidden.
   *
   * It works because of the grid's shape rather than in spite of it. A cell is
   * keyed by ARRIVAL and LENGTH, so "no arrivals on the 16th" is a row and "no
   * departures on the 16th" is a diagonal — every cell whose arrival plus
   * length lands there. Both are expressible without inventing a field, which
   * is the argument for saying "closed to arrival" rather than deleting a day
   * rate: an absent price reads to a channel as unavailable, with no reason
   * attached and nothing for the park to correct.
   *
   * Absent means no rules, which is what every caller did before this existed.
   */
  restrictions?: readonly Restriction[];
  /** Identifier of the programme version, stamped onto every cell. */
  source: string;
  /** Called every `progressEvery` cells, for long runs. */
  onProgress?: (done: number, total: number) => void;
  progressEvery?: number;
}

export interface MaterialiseResult {
  cells: PublishedCell[];
  stats: {
    total: number;
    bookable: number;
    onRequest: number;
    closed: number;
    /** Wall-clock milliseconds, so a caller can size a batch window. */
    ms: number;
  };
}

/** The party as the park counted it. Stable, sorted, and lossless. */
function partyKey(q: Record<string, number> | undefined): string {
  if (!q) return '';
  return Object.keys(q).sort().map((k) => `${k}:${q[k]}`).join(',');
}

/**
 * The party as a channel counts it.
 *
 * The one piece of translation in the whole pipeline. A park's categories are
 * its own business right up to the moment a price leaves the building, at
 * which point it has to be expressed in adults, children and infants, because
 * that is the only vocabulary an OTA has.
 *
 * An undeclared quantity counts as an adult: legacy programmes carry no
 * categories at all, and over-reporting a party is the cheap direction of that
 * mistake — the channel shows the accommodation to fewer searches rather than
 * to parties that do not fit.
 */
function channelKey(set: PieceSet, q: Record<string, number> | undefined): string {
  if (!q) return '';
  let adults = 0; let children = 0; let infants = 0;
  for (const [key, n] of Object.entries(q)) {
    const role = set.guests?.[key]?.channel ?? 'adult';
    if (role === 'other') continue;
    if (role === 'adult') adults += n;
    else if (role === 'child') children += n;
    else infants += n;
  }
  let out = `${adults}A`;
  if (children) out += `${children}C`;
  if (infants) out += `${infants}I`;
  return out;
}

/**
 * Price every (arrival, los) in the window.
 *
 * O(days x lengths) engine calls with no shortcut, on purpose. An earlier
 * attempt computed a whole LOS row from one DP run and disagreed with
 * `priceStay` on 47 of 140 sampled cells, in both directions — a piece may be
 * trimmed to land on the departure and still cost full price, so the answer for
 * n nights genuinely is not a prefix of the answer for N. A fast grid that is
 * quietly wrong is worse than a slow one. See the removed `priceLosRow` note in
 * engine.ts.
 */
export function materialise(set: PieceSet, opts: MaterialiseOptions): MaterialiseResult {
  const t0 = Date.now();
  const cells: PublishedCell[] = [];
  const occ = channelKey(set, opts.quantities);
  const party = partyKey(opts.quantities);
  const stats = { total: 0, bookable: 0, onRequest: 0, closed: 0, ms: 0 };
  const totalCells = Math.max(0, opts.until - opts.from) * opts.lengths.length;
  const every = opts.progressEvery ?? 500;

  for (let date = opts.from; date < opts.until; date++) {
    for (const los of opts.lengths) {
      let q: Quote;
      try {
        q = priceStay(set, { arrival: date, nights: los, quantities: opts.quantities ?? {} });
      } catch {
        // A piece set that throws is a defect, but one bad cell must not abort a
        // whole publish run; record it as closed and let verification surface it.
        cells.push({
          unitType: opts.unitType, date, los, status: 'closed', occupancy: occ, party, source: opts.source,
        });
        stats.total++; stats.closed++;
        continue;
      }

      // A stay the rules forbid is closed WHATEVER it prices at. Checked after
      // pricing rather than instead of it, because a park still wants to know
      // the number — a restriction is a permission, not a missing tariff.
      const refused = opts.restrictions?.length
        ? checkRestrictions(opts.restrictions, date, los) : null;

      const status: CellStatus = refused !== null ? 'closed'
        : (q.status === 'bookable'
          ? 'bookable' : (q.status === 'onRequest' ? 'onRequest' : 'closed'));
      const cell: PublishedCell = {
        unitType: opts.unitType, date, los, status, occupancy: occ, party, source: opts.source,
      };
      if (status !== 'closed') cell.price = round2(q.total);
      cells.push(cell);

      stats.total++;
      if (status === 'bookable') stats.bookable++;
      else if (status === 'onRequest') stats.onRequest++;
      else stats.closed++;

      if (opts.onProgress && stats.total % every === 0) opts.onProgress(stats.total, totalCells);
    }
  }

  stats.ms = Date.now() - t0;
  return { cells, stats };
}

/** Cents, so a published price never carries binary float noise across a wire. */
function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * A run of consecutive arrival dates that share one price, for one LOS.
 *
 * This is the STORAGE form. A season of 365 dates x 21 lengths is 7,665 cells,
 * of which the vast majority are runs of identical numbers — the market
 * convention (Lodgify, Beds24, NextPax) is to author bands and publish cells,
 * and that is what this pair of functions is.
 */
export interface RateBand {
  unitType: string;
  /** Inclusive. */
  from: Day;
  /** Inclusive — bands are authored, and an inclusive end is what a human
   *  means by "1 July to 31 August". Cells use half-open ranges; bands do not,
   *  and the two are never mixed because they are different types. */
  to: Day;
  los: number;
  price?: number;
  status: CellStatus;
  occupancy: string;
  party?: string;
  source: string;
}

/** Collapse dense cells into bands. Lossless — `expandBands` inverts it. */
export function compressToBands(cells: PublishedCell[]): RateBand[] {
  const byKey = new Map<string, PublishedCell[]>();
  for (const c of cells) {
    const k = `${c.unitType}|${c.los}|${c.occupancy}|${c.party ?? ''}`;
    const list = byKey.get(k);
    if (list) list.push(c); else byKey.set(k, [c]);
  }

  const bands: RateBand[] = [];
  for (const list of byKey.values()) {
    list.sort((a, b) => a.date - b.date);
    let open: RateBand | null = null;
    for (const c of list) {
      const same = open !== null
        && c.date === open.to + 1
        && c.status === open.status
        && c.price === open.price
        && c.source === open.source;
      if (same && open) { open.to = c.date; continue; }
      if (open) bands.push(open);
      open = {
        unitType: c.unitType, from: c.date, to: c.date, los: c.los,
        price: c.price, status: c.status, occupancy: c.occupancy, party: c.party,
        source: c.source,
      };
    }
    if (open) bands.push(open);
  }
  bands.sort((a, b) => a.los - b.los || a.from - b.from);
  return bands;
}

/** Expand bands back into dense cells. */
export function expandBands(bands: RateBand[]): PublishedCell[] {
  const cells: PublishedCell[] = [];
  for (const b of bands) {
    for (let d = b.from; d <= b.to; d++) {
      const cell: PublishedCell = {
        unitType: b.unitType, date: d, los: b.los, status: b.status,
        occupancy: b.occupancy, party: b.party, source: b.source,
      };
      if (b.price !== undefined) cell.price = b.price;
      cells.push(cell);
    }
  }
  cells.sort((a, b) => a.date - b.date || a.los - b.los);
  return cells;
}

export interface VerifyResult {
  checked: number;
  mismatches: Array<{ date: Day; los: number; cell?: number; engine?: number; why: string }>;
  ok: boolean;
}

/**
 * Re-price a sample of cells against the engine.
 *
 * The failure this exists to catch is drift: a cell published from programme
 * version A still sitting in the grid after the programme moved to version B.
 * That is invisible by inspection and expensive in a dispute, so it gets a
 * check rather than a convention.
 */
export function verifyCells(
  set: PieceSet, cells: PublishedCell[],
  quantities: Record<string, number> | undefined,
  sample = 400,
): VerifyResult {
  const step = Math.max(1, Math.floor(cells.length / sample));
  const mismatches: VerifyResult['mismatches'] = [];
  let checked = 0;

  for (let i = 0; i < cells.length; i += step) {
    const c = cells[i];
    checked++;
    let q: Quote;
    try {
      q = priceStay(set, { arrival: c.date, nights: c.los, quantities: quantities ?? {} });
    } catch (err) {
      mismatches.push({ date: c.date, los: c.los, why: `engine threw: ${(err as Error).message}` });
      continue;
    }
    const status: CellStatus = q.status === 'bookable'
      ? 'bookable' : (q.status === 'onRequest' ? 'onRequest' : 'closed');
    if (status !== c.status) {
      mismatches.push({
        date: c.date, los: c.los, why: `status ${c.status} published, engine says ${status}`,
      });
      continue;
    }
    if (status === 'closed') continue;
    const engine = round2(q.total);
    if (c.price === undefined || Math.abs(engine - c.price) > 0.005) {
      mismatches.push({ date: c.date, los: c.los, cell: c.price, engine, why: 'price drift' });
    }
  }

  return { checked, mismatches, ok: mismatches.length === 0 };
}

/** Cheapest bookable price per arrival date, across the published lengths. */
export function lowestPerDate(cells: PublishedCell[]): Map<Day, number> {
  const out = new Map<Day, number>();
  for (const c of cells) {
    if (c.status !== 'bookable' || c.price === undefined) continue;
    const cur = out.get(c.date);
    if (cur === undefined || c.price < cur) out.set(c.date, c.price);
  }
  return out;
}

export { findPerNightRises, type PerNightRise } from './monotonic';
